import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { MenuService } from 'src/app/services/menu/menu.service';
import { PodService } from 'src/app/services/pod/pod.service';
import { UserDataService } from 'src/app/services/userData/user-data.service';
import { UtilService } from 'src/app/services/util/util.service';
import { CompanyService } from 'src/app/services/company/company.service';
import { IconComponent } from '../icon/icon.component';
import { FormsModule } from '@angular/forms';
import { AccordionComponent, AccordionPanelComponent } from 'ngx-bootstrap/accordion';
import { NgStyle, NgClass } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { FilterMenuPipe } from 'src/app/pipes/filter-menu/filter-menu.pipe';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    imports: [IconComponent, FormsModule, AccordionComponent, AccordionPanelComponent, NgStyle, NgClass, MatTooltip, FilterMenuPipe, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  menu = inject(MenuService);
  util = inject(UtilService);
  ud = inject(UserDataService);
  translate = inject(TranslateService);
  ps = inject(PodService);
  cs = inject(CompanyService);
  private router = inject(Router);

  nameFilter: any = '';
  iconPath = 'https://publicfiles.mybeehome.com/icons/tools/icon-chevron-';
  controlMenuBehavior: boolean;
  firstOpenCategoryIndex: number = 0;
  showMenuFilter: boolean = false;

  get locale(): string {
    return this.menu?.loggedUser?.preferredLocale || 'pt-BR';
  }

  showDealerCodeModal: boolean = false;
  loadingSsoBroker: boolean = false;
  loadingDealerCodes: boolean = false;
  selectedDealerCode: string = null;
  selectedPodForSsoBroker: any = null;
  dealerCodes: any[] = [];


  constructor() {
    if (this.util.configuration.tenantId == 'solarbr') {
      this.showMenuFilter = true;
    }

    if (localStorage.getItem('menu')) {
      this.menu.sidebarItems = JSON.parse(localStorage.getItem('menu'));
    }
  }

  cancelSearch() {
    this.nameFilter = null;
  }

  ngOnInit(): void {
    this.controlMenuBehavior = this.util.configuration.controlMenuBehavior;

    // Zoneless: detectChanges() é síncrono, markForCheck() não é confiável
    // em callbacks externos (socket/HTTP). Garante re-render imediato da bubble.
    this.menu.unreadUpdated$.subscribe(() => {
      this.cdr.detectChanges();
    });

    this.loadMenu();
  }

  private loadMenu(): void {
    const navigateToFirstPod = () => {
      if (this.router.url === '/') {
        const pod = this.menu.getAllFirstPod(this.menu.sidebarItems);
        this.navigateToPod(pod);
      }
    };

    if (this.menu.loggedUser) {
      this.menu.getLeftMenu().then(serverMenu => {
        this.menu.sidebarItems = serverMenu;
        this.cdr.markForCheck();
        navigateToFirstPod();
      });
    } else {
      this.menu.getLoggedUser().then(() => {
        this.menu.getLeftMenu().then(serverMenu => {
          this.menu.sidebarItems = serverMenu;
          this.cdr.markForCheck();
          navigateToFirstPod();
        });
      });
    }
  }

  toggleMenuFilter() {
    this.showMenuFilter = !this.showMenuFilter;
  }

  onClickPod(podId: number, event?: Event) {
    const selectedPod = this.menu.sidebarItems.flatMap((category) => category.podTOs).find((pod) => pod.id === podId);
    if (!selectedPod) {
      return;
    }

    if (event) {
      event.preventDefault();
    }

    this.menu.hasPermissionToAccessPod(selectedPod.podType, podId).subscribe((data: any) => {
        if(JSON.parse(data) == false) {
          this.updateUserAndReloadMenu();
          return;
        }

      this.menu.sidebarItems.forEach(category => {
        if (category && category.podTOs) {
          category.podTOs.forEach(p => p.selected = (p.id === podId));
        }
      });

        // audit removido daqui — paginas de destino ja chamam insertPodAccessAudit via
        // getPodWithOperation() em menu.service.ts. clicar no sidebar gerava 2 chamadas a
        // /api/pod/audit/. casos de deep link continuam funcionando porque o componente
        // de destino sempre chama getPodWithOperation. casos especiais (custom-link,
        // connector) chamam diretamente insertPodAccessAudit no proprio componente.
        this.menu.closeSideBar();
        this.navigateToPod(selectedPod);
        this.cdr.markForCheck();
      },
      (error) => {
        this.updateUserAndReloadMenu();
        this.cdr.markForCheck();
      }
    );
  }

  private navigateToPod(pod: any) {
    switch (pod.podType) {
      case 'wikiPage':
        this.router.navigate(['/wiki', pod.wikiId, 'detail'], { queryParams: { view: 'full' } });
        break;
      
      case 'internalLink':
        this.router.navigateByUrl(pod.about);
        break;
      
      case 'blogPost':
        this.router.navigate(['/blog', pod.blogId, 'detail']);
        break;
      
      case 'customLink':

        if (pod.iframeCustomLink) {
          this.router.navigate(['/customLink', pod.id]);
        } else {
          if (pod.includeJwtToken) {
            const jwt = localStorage.getItem('jwt');
            const url = jwt ? `${pod.about}#token=${jwt}` : pod.about;
            window.open(url, '_blank');
          } else {
            window.open(pod.about, '_blank');
          }
        }
        break;
      
      case 'urlIntegration':
        if (pod.queryParam === 'sso_broker') {
          this.selectedPodForSsoBroker = pod;
          this.selectedDealerCode = null;
          this.showDealerCodeModal = true;
          this.loadingDealerCodes = true;
          this.cdr.markForCheck();

          const loggedUserCompanyId = this.menu.loggedUser?.companyId;
          this.cs.listDealerCodes(loggedUserCompanyId).subscribe({
            next: (data: any) => {
              this.dealerCodes = data || [];
              this.loadingDealerCodes = false;
              this.cdr.markForCheck();
            },
            error: () => {
              this.dealerCodes = [];
              this.loadingDealerCodes = false;
              this.cdr.markForCheck();
            }
          });
        } else if (pod.iframeCustomLink) {
          this.router.navigate(['/connector', pod.id, 'iframe']);
        } else {
          // Em mobile/navegador in-app (ex.: portal aberto dentro do webview de um
          // app parceiro) o Safari bloqueia window.open de NOVA ABA. O ConnectorComponent
          // já redireciona na mesma aba (window.location.href), então navegamos same-tab.
          // No desktop mantém a nova aba.
          if (window.innerWidth < 992) {
            this.router.navigate(['/connector', pod.id]);
          } else {
            const url = this.router.serializeUrl(
              this.router.createUrlTree(['/connector', pod.id])
            );
            window.open(url, '_blank');
          }
        }
        break;
      
      default:

        this.router.navigate(['/', pod.podType]);
        break;
    }
  }

  getPodUrl(pod: any): string {
    if (!pod) return 'javascript:void(0)';
    
    switch (pod.podType) {
      case 'wikiPage':
        return this.router.serializeUrl(
          this.router.createUrlTree(['/wiki', pod.wikiId, 'detail'], { queryParams: { view: 'full' } })
        );
      
      case 'internalLink':
        return pod.about || 'javascript:void(0)';
      
      case 'blogPost':
        return this.router.serializeUrl(
          this.router.createUrlTree(['/blog', pod.blogId, 'detail'])
        );
      
      case 'customLink':
        if (pod.iframeCustomLink) {
          return this.router.serializeUrl(
            this.router.createUrlTree(['/customLink', pod.id])
          );
        } else {
          return pod.about || 'javascript:void(0)';
        }
      
      case 'urlIntegration':
        if (pod.iframeCustomLink) {
          return this.router.serializeUrl(
            this.router.createUrlTree(['/connector', pod.id, 'iframe'])
          );
        } else {
          return this.router.serializeUrl(
            this.router.createUrlTree(['/connector', pod.id])
          );
        }
      
      default:
        return this.router.serializeUrl(
          this.router.createUrlTree(['/', pod.podType])
        );
    }
  }
  
  onCategoryToggle(currentCategory: any, event: Event): void {
    event.stopPropagation();
    if (this.controlMenuBehavior) {
      this.menu.sidebarItems.forEach(cat => {
        cat.isopen = cat === currentCategory ? !cat.isopen : false;
      });
    } else {
      currentCategory.isopen = !currentCategory.isopen;
    }
  }

  getUnreadTooltip(pod): string {
    const translationKey = pod?.unread === 1 ? pod?.unreadMessage : pod?.unreadsMessage;
    if (translationKey)
      return this.translate.instant(translationKey);
    else
      return null
  }

  getBackgroundColor(): string | undefined {
    const config = this.util.getStoredConfiguration();
  
    if (!config) return undefined; // Ensure config is not null or undefined
  
    const isBeehome = config.tenantId === 'beehome';
    const hasAbonoControl = config.abonoControlInTimecard;
  
    return (isBeehome && hasAbonoControl) ? config.mobileSwatch : undefined;
  }

  updateUserAndReloadMenu() {
    //# 31/10/2025 Atualiza o user em cache ja que ele teve alguma informação alterada e recarrega o menu para que ele se ajuste de acordo com as novas permissões.
    this.menu.updateLoggedUserCache();
    setTimeout(() => {
      this.reloadMenu();
    }, 1000);
  }

  reloadMenu() {
    this.menu.updateSidebarItems().subscribe(
      () => { this.cdr.markForCheck(); },(error) => { this.cdr.markForCheck(); }
    );
  }

  confirmDealerCode() {
    if (!this.selectedDealerCode) return;
    this.showDealerCodeModal = false;
    this.loadingSsoBroker = true;

    if (this.selectedPodForSsoBroker?.iframeCustomLink) {
      this.router.navigate(
        ['/connector', this.selectedPodForSsoBroker.id, 'iframe'],
        { queryParams: { dealerCode: this.selectedDealerCode } }
      );
    } else {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(
          ['/connector', this.selectedPodForSsoBroker.id],
          { queryParams: { dealerCode: this.selectedDealerCode } }
        )
      );
      window.open(url, '_blank');
    }
  }

  cancelDealerCode() {
    this.showDealerCodeModal = false;
  }

  selectDealerCode(code: string) {
    this.selectedDealerCode = code;
  }

}
